import React from 'react';
import { IRestaurantPropertiesModifierGroup } from 'types';
import { MultipleSelect } from './MultipleSelect';
import { ModifierGroupFromTemplate } from './ModifierGroupFromTemplate';
import { ModifierGroup } from './ModifierGroup';
import style from './ModifierGroups.module.scss';

interface IProps {
	setModifierGroups: (value: string[]) => void;
	setModifierGroupsWithAdditionalData: (value: IRestaurantPropertiesModifierGroup[]) => void;
	refetch: () => void;
	responseError: boolean;
	IDsSelectedModifierGroups: string[];
	fetchedModifierGroups: IRestaurantPropertiesModifierGroup[];
	modifierGroupsWithAdditionalData: IRestaurantPropertiesModifierGroup[];
}

export const ModifierGroups: React.FC<IProps> = React.memo(
	({
		setModifierGroups,
		setModifierGroupsWithAdditionalData,
		refetch,
		responseError,
		IDsSelectedModifierGroups,
		fetchedModifierGroups,
		modifierGroupsWithAdditionalData,
	}) => {
		const handleSetModifierGroups = (value: string[]) => {
			const updatedModifierGroupsWithAdditionalData = value.map((modifierGroupId) => {
				const groupFromPreviousState = modifierGroupsWithAdditionalData.find(
					(exist) => exist.id === modifierGroupId,
				);

				if (groupFromPreviousState) {
					return groupFromPreviousState;
				}

				const fetchedModifierGroup = fetchedModifierGroups.find(
					(fetchedModifierGroup) => fetchedModifierGroup.id === modifierGroupId,
				);
				const newGroup = {
					id: modifierGroupId,
					name: fetchedModifierGroup?.name || '-',
					upLimit: fetchedModifierGroup?.upLimit || null,
					downLimit: fetchedModifierGroup?.downLimit || null,
					inclusive: fetchedModifierGroup?.inclusive || null,
					isLimitsProtected: fetchedModifierGroup?.isLimitsProtected || null,
					isAdditional: fetchedModifierGroup?.isAdditional || null,
					isTemplate: fetchedModifierGroup?.isTemplate || false,
				};
				return newGroup;
			});
			const sortedModifierGroupsWithAdditionalData = [...updatedModifierGroupsWithAdditionalData].sort((a, b) => {
				if (a.isTemplate === b.isTemplate) {
					return 0;
				}
				return a.isTemplate ? -1 : 1;
			});

			setModifierGroups(value);
			setModifierGroupsWithAdditionalData(sortedModifierGroupsWithAdditionalData);
		};

		const changeDownLimit = (id: string, value: Nullable<number>) => {
			const updatedModifierGroupsWithAdditionalData = modifierGroupsWithAdditionalData.map((el) =>
				el.id === id ? { ...el, downLimit: value } : el,
			);
			setModifierGroupsWithAdditionalData(updatedModifierGroupsWithAdditionalData);
		};

		const changeUpLimit = (id: string, value: Nullable<number>) => {
			const updatedModifierGroupsWithAdditionalData = modifierGroupsWithAdditionalData.map((el) =>
				el.id === id ? { ...el, upLimit: value } : el,
			);
			setModifierGroupsWithAdditionalData(updatedModifierGroupsWithAdditionalData);
		};

		const handleRemove = (id: string) => {
			const updatedModifierGroups = IDsSelectedModifierGroups.filter((el) => el !== id);
			const updatedModifierGroupsWithAdditionalData = modifierGroupsWithAdditionalData.filter(
				(modifierGroupWithAdditionalData) => modifierGroupWithAdditionalData.id !== id,
			);
			setModifierGroups(updatedModifierGroups);
			setModifierGroupsWithAdditionalData(updatedModifierGroupsWithAdditionalData);
		};

		return (
			<div className={style.container} data-test="ModifierGroups">
				<MultipleSelect
					data={fetchedModifierGroups || []}
					refetch={refetch}
					responseError={responseError}
					setModifierGroups={handleSetModifierGroups}
					IDsSelectedModifierGroups={IDsSelectedModifierGroups}
				/>
				<div className={style.groupsList}>
					{modifierGroupsWithAdditionalData.map((el, index) =>
						el.isTemplate ? (
							<ModifierGroupFromTemplate
								key={el.id}
								onDelete={handleRemove}
								positionNumber={index + 1}
								label={el.name || '-'}
								modifierGroupId={el.id}
								inclusive={el.inclusive}
								isLimitsProtected={el.isLimitsProtected}
								isAdditional={el.isAdditional}
								lowerLimit={el.downLimit}
								upperLimit={el.upLimit}
							/>
						) : (
							<ModifierGroup
								key={el.id}
								changeDownLimit={changeDownLimit}
								changeUpLimit={changeUpLimit}
								onDelete={handleRemove}
								positionNumber={index + 1}
								label={el.name || '-'}
								modifierGroupId={el.id}
								inclusive={el.inclusive}
							/>
						),
					)}
				</div>
			</div>
		);
	},
);
